import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { askSessionPath } from '@/constants/appRoutes';
import { ChatMessage, RAGCitation } from '../../services/api';
import { useChatSession } from './hooks/useChatSession';
import { useChatContext } from './hooks/useChatContext';
import { useChatConversation } from './hooks/useChatConversation';
import { useChatShell } from './ChatShellContext';
import { ChatHeader } from './components/ChatHeader';
import { ChatMessages } from './components/ChatMessages';
import { AskCommunityLayout } from './components/AskCommunityLayout';
import { ChatComposer } from './components/ChatComposer';
import { ChatEmptyState } from './components/ChatEmptyState';

const CHAT_MAX_WIDTH = 720;

export interface ChatNavigationSeed {
  seedMessages?: ChatMessage[];
  seedCitations?: RAGCitation[];
}

const ChatPage: React.FC = () => {
  const { sessionId: routeSessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { askMode, setShowSources, setCitations } = useChatShell();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [streamStatus, setStreamStatus] = useState<string | null>(null);
  const [sidebarCitations, setSidebarCitationsState] = useState<RAGCitation[]>([]);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const submittingRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [preUploadedIds, setPreUploadedIds] = useState<string[]>([]);
  const prevRouteSessionIdRef = useRef<string | undefined>(undefined);

  const setSidebarCitations = useCallback(
    (citations: RAGCitation[]) => {
      setSidebarCitationsState(citations);
      setCitations(citations);
      setShowSources(citations.length > 0);
    },
    [setCitations, setShowSources],
  );

  const navigateToSession = useCallback(
    (sessionId: string, options?: { replace?: boolean; state?: unknown }) => {
      navigate(askSessionPath(sessionId), { replace: options?.replace, state: options?.state });
    },
    [navigate],
  );

  const {
    contextMode,
    selectedDocs,
    setSelectedDocs,
    selectedChats,
    setSelectedChats,
    selectedProjects,
    setSelectedProjects,
    attachedFiles,
    contextLocked,
    setContextLocked,
    uploadStatusLines,
    setUploadStatusLines,
    handleFileSelect,
    removeAttachedFile,
    removeSelectedDoc,
    removeSelectedChat,
    removeSelectedProject,
  } = useChatContext();

  const {
    sessionId,
    sessionTitle,
    currentSessionRole,
    initialDataLoaded,
    sessionLoading,
  } = useChatSession({
    initialSessionId: routeSessionId ?? null,
    navigateToSession,
    locationState: null,
    onMessagesLoaded: setMessages,
    onSidebarCitationsChange: setSidebarCitations,
    onSelectedDocsInit: setSelectedDocs,
    onSelectedChatsInit: setSelectedChats,
    onSelectedProjectsInit: setSelectedProjects,
    onAttachedUploadIdsInit: setPreUploadedIds,
    onContextLockChange: setContextLocked,
  });

  useEffect(() => {
    const seed = location.state as ChatNavigationSeed | null;
    if (seed?.seedMessages) {
      setMessages(seed.seedMessages);
      setSidebarCitations(seed.seedCitations ?? []);
      navigate(location.pathname, { replace: true, state: null });
      prevRouteSessionIdRef.current = routeSessionId;
      return;
    }
    prevRouteSessionIdRef.current = routeSessionId;
  }, [routeSessionId, location.state, location.pathname, navigate, setSidebarCitations]);

  const { handleSubmit, handleKeyDown } = useChatConversation({
    initialQuery: '',
    initialDataLoaded,
    sessionId,
    setSessionId: () => {},
    navigateToSession,
    locationState: null,
    currentSessionRole,
    messages,
    setMessages,
    input,
    setInput,
    loading,
    setLoading,
    streamingMessageId,
    setStreamingMessageId,
    setStreamStatus,
    sidebarCitations,
    setSidebarCitations,
    contextMode,
    selectedDocs,
    selectedChats,
    selectedProjects,
    attachedFiles,
    preUploadedIds,
    setContextLocked,
    uploadStatusLines,
    setUploadStatusLines,
    messagesEndRef,
    submittingRef,
  });

  const isEmptyComposer = messages.length === 0 && !loading && !sessionLoading;

  const chatInput = (
      <ChatComposer
        input={input}
        onInputChange={setInput}
        onAiSubmit={handleSubmit}
        onKeyDown={handleKeyDown}
        currentSessionRole={currentSessionRole}
        sessionId={sessionId}
        loading={loading || sessionLoading}
        contextLocked={contextLocked}
        selectedDocs={selectedDocs}
        selectedChats={selectedChats}
        selectedProjects={selectedProjects}
        attachedFiles={attachedFiles}
        onRemoveDoc={removeSelectedDoc}
        onRemoveChat={removeSelectedChat}
        onRemoveProject={removeSelectedProject}
        onRemoveFile={removeAttachedFile}
        uploadStatusLines={uploadStatusLines}
        fileInputRef={fileInputRef}
        onFileSelect={handleFileSelect}
        autoFocus={isEmptyComposer}
        prominent={isEmptyComposer && askMode !== 'community'}
      />
  );

  if (askMode === 'community') {
    return <AskCommunityLayout>{chatInput}</AskCommunityLayout>;
  }

  if (isEmptyComposer) {
    return <ChatEmptyState onSelectPrompt={setInput}>{chatInput}</ChatEmptyState>;
  }

  return (
    <>
      <ChatHeader title={sessionTitle} />
      <ChatMessages
        messages={messages}
        loading={loading}
        sessionLoading={sessionLoading}
        sessionLoadKey={routeSessionId ?? null}
        streamingMessageId={streamingMessageId}
        streamStatus={streamStatus}
        messagesEndRef={messagesEndRef}
        maxWidth={CHAT_MAX_WIDTH}
      />
      {chatInput}
    </>
  );
};

export default ChatPage;
